/**
 * Unified schedule results tab content (baseball, racing, generic).
 * Uses useScheduleResultsTab and getScheduleResultsConfig; renders desktop table + mobile accordion.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Disclosure, Transition } from '@headlessui/react';
import { getSportColor } from 'shared/utils/sport-colors';
import { UserSessionContext } from 'features/session/userSessionStore';
import { vsMarketTypeKey } from 'lib/virtualSportsI18n';
import { cn } from 'lib/utils';
import { useScheduleResultsTab, formatTime } from 'features/markets/components/bets-area/useScheduleResultsTab';
import { getScheduleResultsConfig } from 'features/markets/components/bets-area/scheduleResultsConfig';

export default function ScheduleResultsTabContent({ sportId }) {
  const { t } = useTranslation();
  const [{ user }] = React.useContext(UserSessionContext);
  const oddsFormat = user?.odds_format || 'decimal';

  const {
    results,
    resultsWithId,
    isLoading,
    isError,
    refetch,
    isRefetching,
    hasNextPage,
    isFetchingNextPage,
    loadMoreSentinelRef,
    n_runners,
    scheduleId,
  } = useScheduleResultsTab(sportId);

  const config = React.useMemo(() => getScheduleResultsConfig(sportId), [sportId]);
  const marketColumns = React.useMemo(
    () => config.getMarketColumns(results),
    [config, results]
  );
  const relevantMarketId = React.useMemo(
    () => config.getRelevantMarketId(marketColumns),
    [config, marketColumns]
  );

  const sportColor = getSportColor(sportId);
  const cellOptions = React.useMemo(
    () => ({
      t,
      sportId,
      n_runners: n_runners ?? 6,
      oddsFormat,
      showOdds: true,
    }),
    [t, sportId, n_runners, oddsFormat]
  );

  if (scheduleId == null) {
    return (
      <div className="p-4 text-sm text-muted-foreground bg-muted/30 rounded-lg border border-border min-h-[120px] flex items-center justify-center">
        <span>{t('NO_SCHEDULE_SELECTED') || 'No schedule selected. Select a schedule above to see results.'}</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4 text-sm text-destructive flex flex-col gap-2">
        <span>{t('ERROR_LOADING_DATA') || 'Failed to load data.'}</span>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isRefetching}
          className="self-start px-3 py-1.5 rounded-md text-xs font-medium bg-destructive/10 text-destructive hover:bg-destructive/20 focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-2 disabled:opacity-50"
        >
          {isRefetching ? (t('LOADING') || 'Loading…') : (t('RETRY') || 'Try again')}
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4 text-sm text-muted-foreground bg-muted/30 rounded-lg border border-border min-h-[120px] flex items-center justify-center">
        {t('LOADING') || 'Loading…'}
      </div>
    );
  }

  if (!results?.length) {
    return (
      <div className="w-full min-h-[120px]">
        <div
          className="mx-4 mt-4 mb-4 flex items-start gap-3 rounded-lg border border-border bg-muted/30 px-4 py-4"
          style={{ borderLeftWidth: '4px', borderLeftColor: sportColor }}
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <i className="bi bi-info-lg text-lg" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">
              {t('NO_RESULTS_YET') || 'No results yet.'}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('NO_RESULTS_YET_DESCRIPTION') || 'There are no completed events in this schedule yet.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-[240px] overflow-hidden rounded-b-lg border border-t-0 border-border">
      {/* Desktop: full table */}
      <div className="max-lg:hidden overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-muted/70 text-left border-b-2 border-border">
              <th className="px-3 py-2.5 font-semibold text-foreground whitespace-nowrap w-14">
                {t('TIME') || 'Time'}
              </th>
              <th className="px-3 py-2.5 font-semibold text-foreground whitespace-nowrap min-w-[5rem]">
                {t(config.eventColumnLabelKey) || config.eventColumnLabelKey}
              </th>
              {marketColumns.map((mid) => (
                <th
                  key={mid}
                  className="px-3 py-2.5 font-semibold text-foreground border-l border-border whitespace-nowrap"
                >
                  {t(vsMarketTypeKey(mid, false))}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {resultsWithId.map((row, index) => (
              <tr
                key={`${row.event_id}-${index}`}
                className={cn(
                  'border-b border-border transition-colors',
                  index % 2 === 0 ? 'bg-background' : 'bg-muted/20',
                  'hover:bg-muted/30'
                )}
              >
                <td className="px-3 py-2 text-muted-foreground whitespace-nowrap tabular-nums font-medium">
                  {formatTime(row.event_timestamp)}
                </td>
                <td
                  className="px-3 py-2 font-medium text-foreground truncate max-w-[10rem]"
                  title={typeof config.renderEventCell(row, { t }) === 'string' ? config.renderEventCell(row, { t }) : ''}
                >
                  {config.renderEventCell(row, { t })}
                </td>
                {marketColumns.map((mid) => (
                  <td
                    key={mid}
                    className={cn(
                      'px-3 py-2 border-l border-border align-middle',
                      sportId === 40 && 'text-gray-900'
                    )}
                  >
                    {config.renderMarketCell(row, mid, cellOptions)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: accordion per event */}
      <div className="lg:hidden divide-y divide-border">
        {resultsWithId.map((row, index) => (
          <Disclosure key={`${row.event_id}-${index}`}>
            {({ open }) => (
              <>
                <Disclosure.Button
                  className={cn(
                    'w-full flex flex-row items-center gap-2 sm:gap-3 px-3 py-2.5 text-left transition-colors min-w-0 font-semibold',
                    open ? 'bg-muted/40' : 'bg-muted/20 hover:bg-muted/30'
                  )}
                >
                  <span className="text-muted-foreground text-sm tabular-nums shrink-0">
                    {formatTime(row.event_timestamp)}
                  </span>
                  <span className="font-medium text-foreground text-sm truncate min-w-0">
                    {config.renderEventCell(row, { t })}
                  </span>
                  {relevantMarketId != null && (
                    <span className="inline-flex items-center gap-1 shrink-0 ml-auto">
                      {config.showRelevantMarketLabelInMobileSummary ? (
                        <>
                          <span className="text-muted-foreground shrink-0">
                            {t(vsMarketTypeKey(relevantMarketId, false))}:
                          </span>
                          <span className="text-foreground truncate">
                            {config.renderMarketCell(row, relevantMarketId, { ...cellOptions, showOdds: false })}
                          </span>
                        </>
                      ) : sportId === 40 ? (
                        <span className="text-gray-900 truncate">
                          {config.renderMarketCell(row, relevantMarketId, { ...cellOptions, showOdds: true })}
                        </span>
                      ) : (
                        config.renderMarketCell(row, relevantMarketId, { ...cellOptions, showOdds: false })
                      )}
                    </span>
                  )}
                  <span
                    className={cn(
                      'inline-flex shrink-0 transition-transform ml-auto',
                      open && 'rotate-180'
                    )}
                    aria-hidden
                  >
                    <i className="bi bi-chevron-down text-muted-foreground" />
                  </span>
                </Disclosure.Button>
                <Transition
                  show={open}
                  enter="transition ease-out duration-200"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="transition ease-in duration-150"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Disclosure.Panel static>
                    <div className="px-3 pb-3 pt-1 space-y-2 bg-background border-t border-border">
                      {marketColumns.map((mid) => (
                        <div
                          key={mid}
                          className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-border last:border-b-0"
                        >
                          <span className="text-sm font-medium text-foreground">
                            {t(vsMarketTypeKey(mid, false))}
                          </span>
                          <span className={cn('text-sm', sportId === 40 && 'text-gray-900')}>
                            {config.renderMarketCell(row, mid, cellOptions)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </Disclosure.Panel>
                </Transition>
              </>
            )}
          </Disclosure>
        ))}
      </div>
      {hasNextPage && <div ref={loadMoreSentinelRef} className="h-4 w-full" aria-hidden />}
      {isFetchingNextPage && (
        <div className="flex justify-center py-2 text-sm text-muted-foreground">{t('LOADING') || 'Loading…'}</div>
      )}
    </div>
  );
}
